/* Progress Jiu-Jitsu, behaviour for the custom homepage sections.
   Scope is deliberately narrow: Prestige owns the header, announcement bar,
   cart drawer and everything else. This file only drives the rail counters
   ("1/2") on the product, category, athlete and editorial rails. */

(function () {
  'use strict';

  function initRail(rail) {
    var section = rail.closest('.pj');
    var text = section && section.querySelector('[data-pj-rail-counter-text]');
    if (!text) return;

    var counter = section.querySelector('[data-pj-rail-counter]');

    // Count pages from the cards themselves. Measuring raw scrollWidth against
    // clientWidth over-counts, because the trailing gap pushes a full row a few
    // pixels past the viewport and reads as an extra page.
    function perView() {
      var first = rail.firstElementChild;
      if (!first) return 1;
      var gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      var step = first.getBoundingClientRect().width + gap;
      if (!step) return 1;
      // Floor, not round: a deliberately half-visible card is a hint that there
      // is more to swipe, not a slot in the current page. The 0.15 tolerance
      // absorbs sub-pixel rounding on exact counts like 3 per view.
      return Math.max(1, Math.floor(rail.clientWidth / step + 0.15));
    }

    function pages() {
      var count = rail.children.length;
      return count ? Math.ceil(count / perView()) : 1;
    }

    function update() {
      var total = pages();
      if (total <= 1) {
        if (counter) counter.style.visibility = 'hidden';
        return;
      }
      if (counter) counter.style.visibility = '';
      var current = Math.round(rail.scrollLeft / rail.clientWidth) + 1;
      text.textContent = Math.min(current, total) + '/' + total;
    }

    rail.addEventListener('scroll', function () {
      window.clearTimeout(rail._pjTimer);
      rail._pjTimer = window.setTimeout(update, 60);
    }, { passive: true });

    window.addEventListener('resize', update);
    update();
  }

  function boot(scope) {
    (scope || document).querySelectorAll('[data-pj-rail]').forEach(initRail);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  // Re-bind when the theme editor swaps a section in.
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();

/* --------------------------------------------------------------------------
   PDP: keep the accordions from running past the bottom of the gallery.

   Sets --pj-accordion-max on Prestige's .accordion-group to the distance from
   its top down to the foot of the gallery, so an opened panel scrolls inside
   that space rather than stretching the column. The floor is the height of the
   closed rows: if the buy box is already taller than the image, the titles
   still show in full and the section is simply allowed to overhang.

   Re-measured on resize and whenever a panel opens, and on image load, since
   the gallery's height is not known until the photograph has decoded.
   -------------------------------------------------------------------------- */
(function () {
  var mq = window.matchMedia('(min-width: 990px)');

  /* The chip used to be pinned to .product-gallery, which is taller than the
     picture, so its offset from the bottom was measured here. It is appended
     into .product-gallery__image-list now, so the picture IS its container and
     a plain 12px in CSS is correct. The measurement stayed behind and, once
     the thumbnails moved under the image, started reporting the height of the
     thumbnail row: the model note floated about a third of the way up the
     photograph on a phone. Reported 8 Oct. */

  function apply() {
    var gallery = document.querySelector('.shopify-section--main-product .product-gallery');
    var group = document.querySelector('.shopify-section--main-product .accordion-group');
    if (!gallery || !group) return;

    if (!mq.matches) {
      group.style.removeProperty('--pj-accordion-max');
      return;
    }

    // Whatever sits below the group inside the column, padding included. The
    // ceiling has to allow for it, or the column still overhangs the image by
    // that much with the accordions open.
    var info = group.closest('.product-info');
    var trailing = info
      ? info.getBoundingClientRect().bottom - group.getBoundingClientRect().bottom
      : 0;

    var room = gallery.getBoundingClientRect().bottom
             - group.getBoundingClientRect().top
             - trailing;

    var rows = 0;
    group.querySelectorAll('summary').forEach(function (s) {
      rows += s.getBoundingClientRect().height;
    });

    group.style.setProperty('--pj-accordion-max', Math.round(Math.max(room, rows)) + 'px');
  }

  function start() {
    var gallery = document.querySelector('.shopify-section--main-product .product-gallery');
    if (!gallery) return;

    apply();
    /* The second observer that used to live here watched the image list for the
       chip offset. That measurement went on 20 Oct when the chip moved inside
       the image list, but the observer was left behind pointing at a function
       that no longer exists: "chipOffset is not defined" threw on every product
       page and killed the rest of this block. Found 27 Oct while testing
       something else. apply() below already watches the gallery itself. */
    addEventListener('resize', apply);
    (mq.addEventListener ? mq.addEventListener.bind(mq, 'change') : mq.addListener.bind(mq))(apply);

    if (window.ResizeObserver) new ResizeObserver(apply).observe(gallery);
    gallery.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) img.addEventListener('load', apply, { once: true });
    });
    document.addEventListener('toggle', function (e) {
      if (e.target.closest && e.target.closest('.accordion-group')) apply();
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();

/* --------------------------------------------------------------------------
   PDP: the price on the buy button, the way Reigning Champ does it and the way
   the Figma draws it. "ADD TO BAG - £42.00" rather than "Add to cart".

   The price itself needs no help. Prestige re-renders the whole product section
   on a variant change, replacing the price node and the button, so the headline
   follows the variant on its own.

   That same re-render is what this has to survive: the button is a NEW element
   each time, carrying Prestige's default label again, so an observer bound to
   the button would die with it. The section is watched instead.

   The current variant's own JSON sits in the variant picker and is re-rendered
   with everything else, so it is always the right variant, with no fetch and
   no variant map to keep in step. Sold out is left exactly as Prestige has it.
   -------------------------------------------------------------------------- */
(function () {
  var section = document.querySelector('.shopify-section--main-product');
  if (!section) return;

  var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP';
  function money(cents) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  var writing = false;
  var busy = false;
  var chosen = new URLSearchParams(location.search).has('variant');

  function apply() {
    if (writing || busy) return;

    var button = section.querySelector('buy-buttons button[type="submit"], .product-form button[type="submit"]');
    if (!button || button.disabled) return;

    /* A bundle has no variant picker: one product, one variant, and the sizes
       are Simple Bundles selects posting line item properties. Without this the
       button kept Prestige's "Add to cart" while every other PDP said "Add to
       bag". Same rule as below, plain until the choices are made, then priced.
       The price is lifted from the headline as rendered rather than formatted
       again here, so the two can never disagree. */
    if (!section.querySelector('variant-picker')) {
      var box = section.querySelector('#simple-bundles-io-options, .pj-bundle-options');
      if (!box) return;
      var picks = [].slice.call(box.querySelectorAll('select'));
      var ready = picks.length > 0 && picks.every(function (s) { return s.value; });
      var shown = section.querySelector('.pj-pdp-headline__price');
      var priceText = shown ? shown.textContent.trim() : '';
      var bundleLabel = ready && priceText ? 'Add to bag - ' + priceText : 'Add to bag';
      if (button.textContent.trim() !== bundleLabel) {
        writing = true;
        button.textContent = bundleLabel;
        writing = false;
      }
      return;
    }

    var blob = section.querySelector('variant-picker script[data-variant]');
    if (!blob) return;

    var variant;
    try { variant = JSON.parse(blob.textContent); } catch (e) { return; }
    if (!variant || !variant.available || typeof variant.price !== 'number') return;

    /* The pairing counts towards the price on the button. Without this the
       button said £35 while two items, £70 worth, went into the bag: raised at
       the theme meeting on 16 Sep. This function is the only writer of the
       label, which is why the total belongs here rather than in the pair block
       (an earlier attempt to write it from there was simply overwritten by this
       one on the next variant change). */
    var pairPence = 0;
    var pairEl = document.querySelector('pj-pair-offer');
    if (pairEl && typeof pairEl.pairPence === 'function') pairPence = pairEl.pairPence();

    /* A wholesale customer sees their own price in the headline, so the button
       has to agree with it. The headline carries both figures when a trade
       price applies, and the ratio is applied here rather than re-running the
       app's Liquid in JavaScript. Retail customers have no attributes and the
       ratio stays 1. */
    var trade = 1;
    var head = section.querySelector('.pj-pdp-headline__price[data-pj-trade]');
    if (head) {
      var t = parseFloat(head.dataset.pjTrade);
      var r = parseFloat(head.dataset.pjRetail);
      if (t > 0 && r > 0 && t < r) trade = t / r;
    }

    // Plain until a size is chosen, priced afterwards, as RC does it.
    var shownPrice = Math.round((variant.price + pairPence) * trade);
    var label = chosen ? 'Add to bag - ' + money(shownPrice) : 'Add to bag';
    if (button.textContent.trim() === label) return;

    writing = true;
    button.textContent = label;
    writing = false;
  }

  /* Reigning Champ does not preselect a size. The button reads a plain "Add to
     Bag" until one is chosen, and only then gains the price. Prestige picks the
     first available size for you, so the choice is cleared here.

     The form still carries that preselected variant id, so submitting without a
     choice would quietly add whatever Prestige had picked. That is worse than
     any styling problem, so the submit is blocked until a size is chosen and
     the picker is scrolled to instead. RC leaves its button enabled and
     validates on click; this matches that, without the silent-wrong-size risk.

     THE SAME APPLIES ON A MIX-AND-MATCH GI, where the size controls are Simple
     Bundles selects rather than Prestige chips. The app preselects the first
     option exactly as Prestige does, so without this a customer can add a gi
     to the bag having chosen neither the jacket nor the pants and receive
     whichever the app happened to pick. Two chances to get it wrong instead of
     one, on the product where it matters most.

     Those selects are only cleared where the app has given them a placeholder
     option to fall back to. Where it has not, they are left exactly as they
     are: a control this does not understand is left alone rather than
     half-managed. */
  function bundleSizes() {
    var box = document.getElementById('simple-bundles-io-options');
    if (!box) return [];
    return [].slice.call(box.querySelectorAll('select[required]')).filter(function (sel) {
      return [].some.call(sel.options, function (o) { return o.value === ''; });
    });
  }

  function bundleUnchosen() {
    return bundleSizes().filter(function (sel) { return !sel.value; });
  }

  function clearPreselection() {
    if (chosen) return;
    section.querySelectorAll('.variant-picker input[type="radio"]').forEach(function (input) {
      input.checked = false;
    });
    // No change event on purpose. The app reads these at submit, and firing
    // one would tell it a size had been picked when the opposite just happened.
    bundleSizes().forEach(function (sel) { sel.value = ''; });
  }

  function sizeSettled() {
    return chosen && bundleUnchosen().length === 0;
  }

  section.addEventListener('change', function (event) {
    if (event.target.matches('.variant-picker input[type="radio"]')) {
      chosen = true;
    }
    if (sizeSettled()) section.classList.remove('pj-needs-size');
  });

  // The app's selects sit outside our section, so the listener goes on the
  // document rather than on the section that does not contain them.
  document.addEventListener('change', function (event) {
    if (!event.target.matches || !event.target.matches('#simple-bundles-io-options select')) return;
    // A bundle product has one variant and no Prestige picker, so choosing the
    // component sizes is the whole of the choice.
    if (!section.querySelector('.variant-picker input[type="radio"]')) chosen = true;
    if (sizeSettled()) section.classList.remove('pj-needs-size');
    // The button carries the price once every component size is picked.
    apply();
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form.matches('form[action*="/cart/add"]') || !section.contains(form)) return;
    if (sizeSettled()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    section.classList.add('pj-needs-size');

    // Whichever control is actually unanswered, in the order they are read.
    var empty = bundleUnchosen()[0];
    var picker = section.querySelector('variant-picker');
    var target = null, focus = null;

    if (!chosen && picker) {
      target = picker;
      focus = picker.querySelector('input[type="radio"]:not([disabled])');
    } else if (empty) {
      target = empty.closest('#simple-bundles-io-options') || empty;
      focus = empty;
    }

    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (focus) focus.focus();
    }
  }, true);

  /* Confirmation on add, the way Reigning Champ does it: the button goes busy
     while the request is in flight, then says it worked before returning to
     normal. Without it the only feedback is the drawer sliding in, which is
     easy to miss if the drawer is already open or the page has scrolled.

     The completion signal is Prestige's own cart drawer opening, so this does
     not need to know how Prestige talks to the Cart API, and it stays correct
     if that changes. If the drawer never opens, the busy state times out on its
     own rather than leaving the button stuck. */
  var restore;

  function setState(state) {
    var button = section.querySelector('buy-buttons button[type="submit"], .product-form button[type="submit"]');
    if (!button) return;
    button.classList.toggle('pj-btn-busy', state === 'busy');
    button.classList.toggle('pj-btn-added', state === 'added');
  }

  function finish() {
    clearTimeout(restore);
    setState('added');
    restore = setTimeout(function () {
      busy = false;
      setState(null);
      apply();
    }, 1600);
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form.matches('form[action*="/cart/add"]') || !section.contains(form)) return;
    if (!chosen) return;   // the guard above handles this case

    busy = true;
    setState('busy');
    // A failed add would otherwise leave the button spinning for ever.
    clearTimeout(restore);
    restore = setTimeout(function () { busy = false; setState(null); apply(); }, 6000);
  });

  var drawer = document.querySelector('cart-drawer');
  if (drawer) {
    new MutationObserver(function () {
      var open = drawer.hasAttribute('open') || drawer.classList.contains('is-open');
      if (open && busy) finish();
    }).observe(drawer, { attributes: true, attributeFilter: ['open', 'class'] });
  }

  /* Colour as a line item property, so it reaches the cart, the order and the
     packing slip. Colourways are separate products and the only option is the
     size, so the line would otherwise read "The Academy Gi, A0" and the
     warehouse would have no idea which one to pick. Reported 27 Oct.

     An input inside the form rather than something bolted onto the submit:
     Prestige posts the form as FormData, so anything inside it is included,
     including the quantity stepper in the drawer later on. Re-applied after
     every re-render, because Prestige replaces the form on a variant change. */
  function ensureColour() {
    var head = section.querySelector('[data-pj-colour]');
    var form = section.querySelector('form[action*="/cart/add"]');
    if (!head || !form) return;

    var colour = head.getAttribute('data-pj-colour');
    if (!colour) return;

    var input = form.querySelector('input[name="properties[Colour]"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[Colour]';
      form.appendChild(input);
    }
    if (input.value !== colour) input.value = colour;
  }

  new MutationObserver(function () {
    clearPreselection();
    ensureColour();
    apply();
  }).observe(section, { childList: true, subtree: true });

  clearPreselection();
  ensureColour();
  apply();
})();

/* --------------------------------------------------------------------------
   Collection page: the filter drawer and the Model / Product view toggle.

   The filter form works without any of this. The panel is a real form posting
   Shopify's own filter parameters, so with JavaScript off it is simply part of
   the page and Apply submits it. What follows only turns it into a drawer.
   -------------------------------------------------------------------------- */
(function () {
  var root = document.querySelector('[data-pj-collection]');
  if (!root) return;

  /* ---- filter drawer ---- */
  var panel = document.querySelector('[data-pj-filter-panel]');
  var openBtn = root.querySelector('[data-pj-filter-open]');

  if (panel && openBtn) {
    panel.hidden = false;              // it was hidden so it never flashes open
    panel.classList.add('pj-filter--drawer');

    var lastFocus;
    function open() {
      lastFocus = document.activeElement;
      panel.classList.add('is-open');
      openBtn.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';
      var first = panel.querySelector('input, button, a');
      if (first) first.focus();
    }
    function close() {
      panel.classList.remove('is-open');
      openBtn.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
      if (lastFocus) lastFocus.focus();
    }

    openBtn.addEventListener('click', open);
    panel.addEventListener('click', function (e) {
      if (e.target.closest('[data-pj-filter-close]') || e.target === panel) close();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && panel.classList.contains('is-open')) close();
    });
  }

  /* ---- Load more, and infinite scroll ----
     The link is a real next-page URL, so it works without any of this. Here it
     fetches that page, lifts the cards out and appends them, then takes the
     next link from the page it just fetched. On failure it stops interfering
     and lets the browser follow the link, so the catalogue is never a dead end. */
  var more = root.querySelector('[data-pj-load-more]');
  var grid = root.querySelector('[data-pj-grid]');

  if (more && grid) {
    function loadNext(link) {
      if (!link || link.getAttribute('aria-busy') === 'true') return;
      link.setAttribute('aria-busy', 'true');

      fetch(link.href)
        .then(function (res) {
          if (!res.ok) throw new Error(res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var nextGrid = doc.querySelector('[data-pj-grid]');
          if (!nextGrid) throw new Error('no grid');

          while (nextGrid.firstElementChild) grid.appendChild(nextGrid.firstElementChild);

          var nextLink = doc.querySelector('[data-pj-load-more]');
          if (nextLink) {
            /* getAttribute, not .href. The fetched page is parsed with no base
               URL, so .href came back as the string we started with and the
               loader kept asking for page 2 for ever. */
            link.setAttribute('href', nextLink.getAttribute('href'));
            link.removeAttribute('aria-busy');
            /* The sentinel sits below the grid, so appending a page usually
               leaves it on screen again and nothing would trigger the next
               one. Re-observed on a short delay rather than immediately: back
               to back, the observer fires again before the new cards have been
               laid out and the whole collection arrives in one go. */
            setTimeout(watch, 200);
          } else {
            link.parentElement.remove();
          }
          // Keep the browser's address bar in step, so a reload or a share
          // does not silently drop back to page one.
          try { history.replaceState({}, '', link.href); } catch (e) {}
        })
        .catch(function () {
          link.removeAttribute('aria-busy');
          window.location.href = link.href;
        });
    }

    /* Infinite scroll, asked for on 20 Oct. The link stays in the markup and
       still works: it is the no-JavaScript path and the thing crawlers follow,
       it is just hidden once this takes over, so a shopper sees the next row
       arrive rather than a button.

       An observer, not a scroll handler, so nothing runs on a frame where the
       grid is nowhere near the bottom. rootMargin starts the fetch 600px out,
       which on a normal connection means the cards are there by the time the
       shopper reaches the gap.

       Safety: after 20 automatic pages it stops and shows the button again.
       A collection with 2,000 products should not be able to load itself into
       a phone until the tab dies. */
    var auto = 0;
    var io = null;

    function watch() {
      if (!io || !more.isConnected) return;
      io.unobserve(more);
      io.observe(more);
    }

    more.addEventListener('click', function (event) {
      event.preventDefault();
      loadNext(more);
    });

    if ('IntersectionObserver' in window) {
      more.closest('.pj-coll__more').classList.add('is-auto');
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          if (auto >= 20) {
            more.closest('.pj-coll__more').classList.remove('is-auto');
            io.disconnect();
            return;
          }
          auto += 1;
          loadNext(more);
        });
      }, { rootMargin: '600px 0px' });
      watch();
    }
  }

  /* ---- Model / Product view ----
     Swaps every card to its second image. The toggle stays hidden unless at
     least one card actually has one, so it never offers a view that would
     change nothing. The choice is remembered for the session, because having
     to set it again on every collection is the sort of thing that makes a
     toggle not worth using. */
  var toggle = root.querySelector('[data-pj-view-toggle]');
  if (!toggle) return;

  var cards = [].slice.call(root.querySelectorAll('.pj-product-card'));
  var swappable = cards.filter(function (card) {
    var img = card.querySelector('img[data-pj-alt-src], img');
    return img && img.dataset.pjAltSrc;
  });
  if (!swappable.length) return;

  toggle.hidden = false;

  function setView(view) {
    swappable.forEach(function (card) {
      var img = card.querySelector('img');
      if (!img) return;
      if (!img.dataset.pjMainSrc) {
        img.dataset.pjMainSrc = img.getAttribute('src');
        img.dataset.pjMainSrcset = img.getAttribute('srcset') || '';
      }
      var product = view === 'product';
      img.setAttribute('src', product ? img.dataset.pjAltSrc : img.dataset.pjMainSrc);
      if (img.dataset.pjAltSrcset || img.dataset.pjMainSrcset) {
        img.setAttribute('srcset', product ? (img.dataset.pjAltSrcset || '') : img.dataset.pjMainSrcset);
      }
    });
    toggle.querySelectorAll('[data-pj-view]').forEach(function (b) {
      var on = b.dataset.pjView === view;
      b.classList.toggle('is-current', on);
      b.setAttribute('aria-pressed', String(on));
    });
    try { sessionStorage.setItem('pj-view', view); } catch (e) {}
  }

  toggle.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-pj-view]');
    if (btn) setView(btn.dataset.pjView);
  });

  var saved;
  try { saved = sessionStorage.getItem('pj-view'); } catch (e) {}
  if (saved === 'product') setView('product');

  // Cards appended by Load more have not been through setView, so they would
  // show the model shot while the rest of the grid shows the product one.
  if (grid) {
    new MutationObserver(function () {
      cards = [].slice.call(root.querySelectorAll('.pj-product-card'));
      swappable = cards.filter(function (card) {
        var img = card.querySelector('img');
        return img && img.dataset.pjAltSrc;
      });
      var active = toggle.querySelector('[data-pj-view].is-current');
      if (active) setView(active.dataset.pjView);
    }).observe(grid, { childList: true });
  }
})();

/* An arrow pointing down should go down. When the hero CTA has no link set, it
   scrolls to whatever section follows rather than jumping to the top of the
   page, which is what a bare "#" would do. */
(function () {
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('[data-pj-hero-scroll]');
    if (!link) return;
    var hero = link.closest('.shopify-section');
    var next = hero && hero.nextElementSibling;
    if (!next) return;
    e.preventDefault();
    next.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
})();

/* --------------------------------------------------------------------------
   Recently viewed, and the cart drawer's totals.

   Their own IIFE on purpose. Both of these first went into the collection
   block above, which opens with

     var root = document.querySelector('[data-pj-collection]');
     if (!root) return;

   so they threw "root is not defined" on every page, and because they sat at
   the end of the file, everything in them never ran. Neither belongs to the
   collection page: the rail appears wherever it is placed, and the drawer is
   global. They query the document, and they run everywhere.
   -------------------------------------------------------------------------- */
(function () {
  /* ---- Recently viewed ----
     Two halves that never run on the same page. A product page records its own
     id; any page carrying the rail reads that list back and asks Shopify to
     render those exact products.

     The list lives in localStorage and nothing is sent anywhere. Search &
     Discovery cannot do this on its own: it has no idea what a given visitor
     has opened. What it does give us is a search endpoint that understands
     `id:` queries, so one request renders the whole rail through the same card
     snippet the rest of the build uses. */
  var RV_KEY = 'pj-recently-viewed';

  function rvRead() {
    try { return JSON.parse(localStorage.getItem(RV_KEY)) || []; }
    catch (e) { return []; }   // private mode, or someone else's data in the key
  }

  var headline = document.querySelector('[data-pj-product-id]');
  if (headline) {
    var id = parseInt(headline.dataset.pjProductId, 10);
    if (id) {
      // Most recent first, no duplicates, and capped so the key cannot grow
      // without limit on a long browsing session.
      var seen = rvRead().filter(function (x) { return x !== id; });
      seen.unshift(id);
      try { localStorage.setItem(RV_KEY, JSON.stringify(seen.slice(0, 20))); } catch (e) {}
    }
  }

  // More than one of these can be on a page now: the section, and the rail
  // inside the search drawer. One request fills them all.
  var rails = [].slice.call(document.querySelectorAll('[data-pj-recently-viewed]'))
    .map(function (el) { return { root: el, track: el.querySelector('[data-pj-rv-track]'), limit: parseInt(el.dataset.limit, 10) || 8 }; })
    .filter(function (r) { return r.track; });

  if (rails.length) {
    // The product being looked at is not "recently viewed", it is the page.
    var current = headline ? parseInt(headline.dataset.pjProductId, 10) : null;
    var want = Math.max.apply(null, rails.map(function (r) { return r.limit; }));
    var ids = rvRead()
      .filter(function (x) { return x !== current; })
      .slice(0, want);

    if (ids.length) {
      var q = ids.map(function (x) { return 'id:' + x; }).join(' OR ');
      fetch('/search?type=product&view=pj-cards&q=' + encodeURIComponent(q))
        .then(function (res) {
          if (!res.ok) throw new Error(res.status);
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var items = [].slice.call(doc.querySelectorAll('[data-pj-rv-id]'));
          if (!items.length) return;

          // Search answers in relevance order, which has nothing to do with the
          // order these were looked at, so put them back into our order.
          items.sort(function (a, b) {
            return ids.indexOf(+a.dataset.pjRvId) - ids.indexOf(+b.dataset.pjRvId);
          });
          rails.forEach(function (rail) {
            // Cloned per rail: the same node cannot live in two places, and
            // appending the originals would empty whichever rail ran first.
            items.slice(0, rail.limit).forEach(function (item) {
              rail.track.appendChild(item.cloneNode(true));
            });
            rail.root.hidden = false;
          });
        })
        .catch(function () { /* leave the rail hidden: it is an extra, not the page */ });
    }
  }

  /* ---- Cart totals ----
     Figma 131:1253 puts four rows above the checkout button: Legend Points,
     Subtotal, Shipping Fee and Total. Prestige renders none of them; it shows
     the total inside the button and nothing else.

     Built in JS and injected rather than added to Prestige's section, so no
     Prestige file is forked and a theme update cannot undo it. That also suits
     the points row, which could never be server-rendered: LoyaltyLion only
     exposes anything through its own script.

     Prestige replaces the whole drawer on every cart change, so this watches
     for that and puts the block back. */

  var CART_LABELS = {
    points: 'Legend Points',
    subtotal: 'Subtotal',
    shipping: 'Shipping Fee',
    shippingValue: 'Calculated at checkout',
    total: 'Total'
  };

  /* Figma shows 420 points on a £42 cart, so ten per pound. This is a display
     of what the order should earn, not a number LoyaltyLion has confirmed:
     their SDK exposes a customer's balance, not the points a cart will earn.
     Set to 0 to hide the row until it can be sourced properly. */
  var CART_POINTS_PER_UNIT = 10;

  /* Grouped by the page's own locale, so 1450 reads as 1,450 here and as the
     right separator wherever the store sells. Same locale the money formatter
     uses, so the two rows cannot disagree about what a thousand looks like. */
  function pjNumber(n) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB').format(n);
    } catch (e) {
      return String(n);
    }
  }

  function pjMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency', currency: currency
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  function pjCartTotals(cart) {
    var wrap = document.createElement('div');
    wrap.className = 'pj-cart-totals';
    wrap.setAttribute('data-pj-cart-totals', '');

    var rows = [];
    if (CART_POINTS_PER_UNIT > 0) {
      rows.push([CART_LABELS.points, pjNumber(Math.floor(cart.total_price / 100 * CART_POINTS_PER_UNIT))]);
    }
    rows.push([CART_LABELS.subtotal, pjMoney(cart.items_subtotal_price, cart.currency)]);
    rows.push([CART_LABELS.shipping, CART_LABELS.shippingValue]);
    rows.push([CART_LABELS.total, pjMoney(cart.total_price, cart.currency)]);

    rows.forEach(function (row) {
      var line = document.createElement('div');
      line.className = 'pj-cart-totals__row' + (row[0] === CART_LABELS.points ? ' pj-cart-totals__row--points' : '');
      var label = document.createElement('span');
      label.textContent = row[0];
      var value = document.createElement('span');
      value.textContent = row[1];
      line.appendChild(label);
      line.appendChild(value);
      wrap.appendChild(line);
    });

    return wrap;
  }

  /* The bar's own numbers, so nothing has to be kept in step by hand. Prestige
     puts them on the element: threshold and total-price.

     The threshold arrives in cents once the element has upgraded, and in major
     units in the server-rendered markup. Anything under 1000 is read as major
     units, which is safe for every real threshold: £50 is 50 or 5000, and a
     genuine £1000 free-shipping bar does not exist. */
  // Every bar on the page: the drawer's and, on /cart, the bag page's too.
  function pjShippingProgress() {
    document.querySelectorAll('free-shipping-bar').forEach(function (bar) {
      var threshold = parseFloat(bar.getAttribute('threshold'));
      var total = parseFloat(bar.getAttribute('total-price'));
      if (!threshold || isNaN(total)) return;
      if (threshold < 1000) threshold *= 100;
      var pct = Math.max(0, Math.min(1, total / threshold)) * 100;
      bar.style.setProperty('--pj-ship-progress', pct.toFixed(2) + '%');
    });
  }
  window.pjShippingProgress = pjShippingProgress;

  /* Header bag count. The header prints it once, server-side, and nothing
     updated it after an add: behind the drawer nobody noticed, but with the
     drawer off for wholesale it sat on the old number. Every add path
     announces the fresh cart (Prestige's cart:change and variant:add carry it;
     cart:refresh does not, so that one asks for it). */
  function pjSetCount(n) {
    document.querySelectorAll('[data-pj-cart-count]').forEach(function (el) {
      el.textContent = n;
      el.hidden = !n;
      var link = el.closest('a');
      if (link) link.setAttribute('aria-label', 'Bag, ' + n + ' items');
    });
  }
  ['cart:change', 'variant:add'].forEach(function (name) {
    document.addEventListener(name, function (e) {
      var cart = e.detail && e.detail.cart;
      if (cart && typeof cart.item_count === 'number') pjSetCount(cart.item_count);
    });
  });
  document.addEventListener('cart:refresh', function () {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) { if (cart) pjSetCount(cart.item_count); })
      .catch(function () {});
  });

  /* PJ Cart extras: the dispatch countdown above the totals and the Trustpilot
     rating under the checkout button. The markup is rendered by that section
     into a <template>, so its text and settings stay in the theme editor; this
     only copies it in, once per rebuild of the drawer. The countdown is then
     filled by PJ Dispatch rules, the same clock the buy box uses. */
  function pjCartExtras(footer) {
    var tpl = document.getElementById('pj-cart-extras');
    if (!tpl || !tpl.content) return;
    var totals = footer.querySelector('[data-pj-cart-totals]');
    var buttons = footer.querySelector('.button-group');

    var dispatch = tpl.content.querySelector('[data-pj-cart-extra="dispatch"]');
    if (dispatch && !footer.querySelector('[data-pj-cart-extra="dispatch"]')) {
      footer.insertBefore(dispatch.cloneNode(true), totals || buttons || null);
    }
    var tp = tpl.content.querySelector('[data-pj-cart-extra="trustpilot"]');
    if (tp && !footer.querySelector('[data-pj-cart-extra="trustpilot"]')) {
      footer.insertBefore(tp.cloneNode(true), buttons ? buttons.nextSibling : null);
    }
    if (window.PJDispatch) window.PJDispatch.renderAll();
  }

  function pjInjectCartTotals() {
    var footer = document.querySelector('.cart-drawer__footer');
    if (!footer) return;                     // empty cart, nothing to total

    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.item_count) return;
        // Re-read: the drawer may have been replaced while this was in flight.
        var live = document.querySelector('.cart-drawer__footer');
        if (!live) return;

        var fresh = pjCartTotals(cart);
        var existing = live.querySelector('[data-pj-cart-totals]');

        if (!existing) {
          var target = live.querySelector('.button-group');
          if (target) live.insertBefore(fresh, target);
          pjCartExtras(live);
          return;
        }
        pjCartExtras(live);

        /* Changing a quantity re-renders parts of the drawer without removing
           our block, so it used to survive holding the old numbers. It is
           refreshed rather than left alone.

           Only when something actually differs, which is what stops this
           feeding itself: our own write is a mutation, the observer fires
           again, the next read matches and nothing is written. */
        if (existing.innerHTML !== fresh.innerHTML) existing.innerHTML = fresh.innerHTML;
      })
      .catch(function () { /* the cart still works without the summary */ });
  }

  var pjDrawer = document.querySelector('cart-drawer');
  if (pjDrawer) {
    pjInjectCartTotals();
    pjShippingProgress();
    // Prestige swaps the drawer's contents wholesale, which takes our block with
    // it. Debounced because a single re-render fires several mutations.
    var pjTotalsTimer;
    new MutationObserver(function () {
      clearTimeout(pjTotalsTimer);
      pjTotalsTimer = setTimeout(function () {
        pjInjectCartTotals();
        pjShippingProgress();
      }, 60);
      /* Attributes as well as children. Opening the drawer changes NOTHING in
         its contents: measured on the live theme, clicking the cart icon fires
         only attribute mutations, `open` among them, with the child count
         unchanged. Watching childList alone meant the totals refreshed when the
         cart was edited but never when the drawer was simply opened, which is
         the one moment they are guaranteed to be looked at.

         Filtered to `open` so the style and aria changes that ride along with
         it do not each trigger a fetch. */
    }).observe(pjDrawer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['open']
    });
  }

  /* ---- Quick add on product cards ----
     Adds through the same protocol as Prestige's own product form, so the cart
     drawer, the header count and our cart extras all update the normal way:
     collect the sections that want re-rendering (cart:prepare-bundled-sections),
     post /cart/add.js with them, then announce variant:add and cart:change with
     the fresh cart. The drawer listens for cart:change and opens itself. */
  function pjQuickAdd(variantId, colour) {
    var sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } }));
    var body = new FormData();
    body.set('id', variantId);
    body.set('quantity', '1');
    // Same property as the product page, so a card add and a PDP add produce
    // the same line rather than two that look different in the cart.
    if (colour) body.set('properties[Colour]', colour);
    if (sections.length) {
      body.set('sections', sections.join(','));
      body.set('sections_url', (window.Shopify && Shopify.routes ? Shopify.routes.root : '/') + 'variants/' + variantId);
    }
    var root = (window.Shopify && Shopify.routes ? Shopify.routes.root : '/');
    var added;
    return fetch(root + 'cart/add.js', { method: 'POST', body: body, headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (res) { return res.json().then(function (json) { return { ok: res.ok, json: json }; }); })
      .then(function (r) {
        if (!r.ok) throw new Error(r.json.description || r.json.message || 'Could not add to bag');
        added = r.json;
        return fetch(root + 'cart.js').then(function (res) { return res.json(); });
      })
      .then(function (cart) {
        cart.sections = added.sections;
        if (!sections.length || (window.themeVariables && themeVariables.settings && themeVariables.settings.cartType === 'page')) {
          window.location.href = root + 'cart';
          return;
        }
        document.documentElement.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: [added], cart: cart } }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cart } }));
      });
  }

  if (!customElements.get('pj-quick-add')) {
    customElements.define('pj-quick-add', class extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;
        this.toggle = this.querySelector('[data-pj-qa-toggle]');
        this.msg = this.querySelector('[data-pj-qa-msg]');

        this.addEventListener('click', (e) => {
          var toggle = e.target.closest('[data-pj-qa-toggle]');
          if (toggle) { e.preventDefault(); this.setOpen(!this.classList.contains('is-open')); return; }
          var btn = e.target.closest('button[data-variant-id]');
          if (btn && !btn.disabled) { e.preventDefault(); this.add(btn); }
        });
        this.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.classList.contains('is-open')) { this.setOpen(false); this.toggle && this.toggle.focus(); }
        });
      }

      setOpen(open) {
        this.classList.toggle('is-open', open);
        if (this.toggle) this.toggle.setAttribute('aria-expanded', String(open));
        if (open) {
          // One open bar at a time, and a tap anywhere else closes it.
          document.querySelectorAll('pj-quick-add.is-open').forEach((qa) => { if (qa !== this) qa.setOpen(false); });
          this.outside = (e) => { if (!this.contains(e.target)) this.setOpen(false); };
          setTimeout(() => document.addEventListener('pointerdown', this.outside), 0);
          var first = this.querySelector('.pj-qa__size:not(:disabled):not(.is-soldout)');
          if (first && matchMedia('(hover: hover)').matches) first.focus({ preventScroll: true });
        } else if (this.outside) {
          document.removeEventListener('pointerdown', this.outside);
          this.outside = null;
        }
      }

      add(btn) {
        if (btn.classList.contains('is-busy')) return;
        btn.classList.add('is-busy');
        btn.setAttribute('aria-busy', 'true');
        if (this.msg) this.msg.textContent = '';
        pjQuickAdd(btn.dataset.variantId, this.getAttribute('data-pj-colour'))
          .then(() => { this.setOpen(false); })
          .catch((err) => {
            if (this.msg) { this.msg.textContent = err.message; this.setOpen(true); }
          })
          .then(() => { btn.classList.remove('is-busy'); btn.removeAttribute('aria-busy'); });
      }
    });
  }

  /* Pandectes cookie banner. Colours, radius, font and spacing are set as
     custom properties on <pandectes-cmp> in progress-custom.css. This covers
     what has no variable: uppercase buttons, Decline as an outline, the
     heading, dropping the cookie icon, and sitting bottom-left instead of in
     the middle of the page. The shadow root is open, so a stylesheet is
     adopted into it. adoptedStyleSheets rather than a <style> node, because
     Pandectes re-renders its root and would sweep a node away. */
  var PJ_CMP_CSS = [
    // Bottom-left, lifted 90px on desktop to clear Attentive's "Get 15% Off"
    // tab below it. Not bottom-right: that is where the cart drawer's checkout
    // button sits, and the banner covered it.
    '.dialog.box{top:auto;bottom:90px;left:var(--actual-float);right:auto;--translate-x:0;--translate-y:0}',
    '@media (max-width:749px){.dialog.box{--actual-float:12px;bottom:12px;left:12px;right:12px;width:auto;max-width:none!important}}',
    '.header{padding-bottom:8px}',
    '.header>h2>.logo{display:none}',
    '.header>h2{font-size:16px;line-height:18px;font-weight:700;letter-spacing:0;color:#000}',
    '.body .message{font-size:13px;line-height:20px;color:#696969}',
    '.body .links a{color:#000;text-decoration:underline;text-underline-offset:3px}',
    'div.actions{flex-direction:row;gap:8px}',
    'div.actions button{flex:1 1 0;min-height:44px;padding:0 18px;border:1px solid #000;border-radius:0;font-size:12px;line-height:1;font-weight:700;letter-spacing:.08em;text-transform:uppercase;box-shadow:none}',
    'div.actions button.filled.deny{background-color:transparent;color:#000}',
    'div.actions button:focus-visible{outline:2px solid #000;outline-offset:2px}'
  ].join('');

  function pjStyleCmp(host) {
    var root = host && host.shadowRoot;
    if (!root || root.__pjStyled) return !!root;
    try {
      var sheet = new CSSStyleSheet();
      sheet.replaceSync(PJ_CMP_CSS);
      root.adoptedStyleSheets = root.adoptedStyleSheets.concat(sheet);
    } catch (e) {
      var style = document.createElement('style');
      style.textContent = PJ_CMP_CSS;
      root.appendChild(style);
    }
    pjFixCmpLinks(root);
    root.__pjStyled = true;
    return true;
  }

  /* The banner's policy link ships as "Learn more" pointing at the
     progress-jj-europe.myshopify.com copy of the privacy policy. Two problems:
     "Learn more" tells a screen reader nothing out of context (Lighthouse
     flags it), and the myshopify domain exposes the internal store address and
     splits the page from its canonical version. Both are fixed here because
     the app gives no setting for either. */
  function pjFixCmpLinks(root) {
    try {
      root.querySelectorAll('a[href*="myshopify.com/policies"], a[href*="/policies/privacy-policy"]').forEach(function (a) {
        a.href = 'https://progressjj.co.uk/policies/privacy-policy';
        if (/^\s*(learn more|more info|read more)\s*$/i.test(a.textContent)) {
          a.textContent = 'Read our privacy policy';
        }
      });
    } catch (e) {}
  }

  (function pjWatchCmp() {
    // The element and its shadow root arrive after load, at a time Pandectes
    // chooses, so watch for it rather than guess a delay. Stops once styled.
    if (pjStyleCmp(document.querySelector('pandectes-cmp'))) return;
    var mo = new MutationObserver(function () {
      if (pjStyleCmp(document.querySelector('pandectes-cmp'))) mo.disconnect();
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
    setTimeout(function () { mo.disconnect(); }, 30000);
  })();

})();
